// "Yeni API anahtarı" drawer'ı.
//
// İki hâlli tek katman: önce ad formu, üretimden sonra DÜZ METİN anahtar.
// Üstüne ikinci bir katman binmiyor: kullanıcı anahtarı kopyalarken önündeki
// tek şey o olmalı.
//
// NOT: "bir kez görürsün" sınırı KALKTI (kullanıcı kararı). Anahtar artık
// kasada şifreli duruyor ve listeden tekrar gösterilebiliyor; bu yüzden buradaki
// uyarılar da "kaybedersen iptal et" demiyor.

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { trTarihSaat } from '../../lib/trFormat'
import Kopyala from './Kopyala'
import { entegrasyon, type AnahtarUretimi } from './api'

export default function AnahtarDrawer({
  kaynakId,
  sayfaId,
  kapsamAdi,
}: {
  kaynakId: string
  /** Doluysa anahtar sayfaya kapsamlanır ve sayfalı URL üretilir. */
  sayfaId?: string
  /** Başlıkta ve toast'ta görünen kapsam ("asya-biyogaz" / "genel_bakis"). */
  kapsamAdi: string
}) {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const [ad, setAd] = useState('')
  const [uretilen, setUretilen] = useState<AnahtarUretimi | null>(null)

  const uret = useMutation({
    mutationFn: () =>
      sayfaId
        ? entegrasyon.sayfaAnahtari(sayfaId, { ad: ad.trim() })
        : entegrasyon.kaynakAnahtari(kaynakId, { ad: ad.trim() }),
    onSuccess: (u) => {
      setUretilen(u)
      kuyruk.invalidateQueries({ queryKey: ['entegrasyon-kaynaklar'] })
      kuyruk.invalidateQueries({ queryKey: ['entegrasyon-kaynak', kaynakId] })
      toast(`${kapsamAdi} · anahtar üretildi — düz metin yalnız bu ekranda`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Anahtar üretilemedi'),
  })

  if (uretilen) {
    return (
      <>
        <DrawerGovde>
          <div className="info-box">
            <div>
              Anahtar üretildi. Kopyalayıp ajan konfigürasyonuna yazın —{' '}
              <b>sonradan da görebilirsiniz</b>: listede <b>{uretilen.anahtar.onek}…</b>
              satırındaki göz simgesi düz metni yeniden açar. Her gösterim denetim
              günlüğüne işlenir.
            </div>
          </div>

          <div className="fld">
            <label>API anahtarı (düz metin)</label>
            <input className="in mono" value={uretilen.duzMetin} readOnly />
            <div className="fld-hint">
              Ajan konfigürasyonunda <b>apiKeyRef</b> alanına yazılır; sahada
              <b> dpapi:</b> ile şifrelenmiş dosyaya alınması önerilir.
            </div>
          </div>

          <div className="fld" style={{ marginTop: 10 }}>
            <label>Uç nokta adresi</label>
            <input className="in mono" value={uretilen.anahtar.ucNoktaUrl} readOnly />
            <div className="fld-hint">
              Ajan konfigürasyonunda <b>agent.post.url</b> alanına yazılır.
            </div>
          </div>

          <div className="mini-list" style={{ marginTop: 12 }}>
            <div className="mini-li">
              <span>Ad</span>
              <span className="vv">{uretilen.anahtar.ad}</span>
            </div>
            <div className="mini-li">
              <span>Kapsam</span>
              <span className="vv">{uretilen.anahtar.sayfaKodu ?? 'Kaynağın tamamı'}</span>
            </div>
            <div className="mini-li">
              <span>Üretim</span>
              <span className="vv">{trTarihSaat(uretilen.anahtar.olusturmaZamani)}</span>
            </div>
          </div>
        </DrawerGovde>

        <DrawerAltlik>
          <Kopyala metin={uretilen.duzMetin} etiket="API anahtarı" />
          <Kopyala metin={uretilen.anahtar.ucNoktaUrl} etiket="Uç nokta adresi" />
          <button
            className="btn btn-pri"
            type="button"
            style={{ flex: 1, justifyContent: 'center' }}
            onClick={() => kapat()}
          >
            Anladım, kapat
          </button>
        </DrawerAltlik>
      </>
    )
  }

  return (
    <>
      <DrawerGovde>
        <div className="info-box">
          <div>
            Üretilen anahtar listede saklanır ve <b>istediğiniz zaman</b> yeniden
            gösterilebilir. Ajan konfigürasyonuna yazmak için kopyalamanız yeterli.
          </div>
        </div>

        <div className="fld">
          <label>Anahtar adı</label>
          <input
            className="in"
            value={ad}
            placeholder="Operatör PC · ProView ajanı"
            onChange={(e) => setAd(e.target.value)}
          />
          <div className="fld-hint">
            Listede bu adla görünür — hangi makineye verildiğini yazın, iptal
            gerektiğinde doğru anahtarı bulmanın tek yolu bu.
          </div>
        </div>

        <div className="rule" style={{ marginTop: 12 }}>
          <b>Kapsam</b>
          <span>
            {sayfaId
              ? `Sayfa (${kapsamAdi}) için üretilir; adres sayfalı URL olur. Paketin kendi page.id'si yine kazanır — uyuşmazlık paketi reddetmez.`
              : `Kaynağın tamamı (${kapsamAdi}) için üretilir; ajan hangi sayfayı tanırsa tanısın aynı adrese gönderir.`}
          </span>
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={uret.isPending || !ad.trim()}
          title={ad.trim() ? undefined : 'Anahtar adı zorunlu'}
          onClick={() => uret.mutate()}
        >
          {uret.isPending ? 'Üretiliyor…' : 'Anahtar üret'}
        </button>
      </DrawerAltlik>
    </>
  )
}
